import { Control, Controller, FieldErrorsImpl, useForm, useWatch } from 'react-hook-form';
import {
  EditMultipleSocialMediaLinkInput,
  editMultipleSocialMediaLinkInputSchema,
  useEditMultipleSocialMediaLinks,
} from '../api/edit-multiple-social-links';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo, useState } from 'react';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import {
  CaseState,
  caseStateMapper,
  ComplianceStatus as ComplianceStatusEnum,
  complianceStatusMapper,
  platformMapper,
  PostType,
  postTypeMapper,
  SocialMediaCategory,
  socialMediaCategoryMapper,
  SocialMediaLink,
  SocialMediaPlatform,
} from '@services/types/api';
import { removedCellClassName } from '@helpers';
import DropdownController from './dropdown-controller';
import {
  DatePickerStyled,
  defaultFormControlStyle,
  StyledBox,
  StyledFormHelperText,
  StyledModal,
} from './styles';
import { categoryOptions, platformOptions } from './constants';
import { Table } from '@molecules';
import { errorMessage, successMessage, TableSelectType } from '@services';
import { Box } from '@mui/material';
import { useParams } from 'react-router-dom';
import InteractButton from '@atoms/Button/InteractButton';
import { EditSocialMediaLinkInput } from '../api/edit-social-links';

interface IGetColumnDataMultipleRow extends Pick<IGetColumnData, 'errors'> {
  control: Control<EditMultipleSocialMediaLinkInput>;
}

interface IEditMultipleSocialLink {
  listEdit: SocialMediaLink[];
  idSelected: string[];
}

interface InteractButtonHandlers {
  isDisabledUpdate: boolean;
  handleClickUpdate: () => Promise<void>;
  handleClickCancel: () => void;
}

export interface IGetColumnData {
  control: Control<EditSocialMediaLinkInput>;
  errors: Partial<
    FieldErrorsImpl<{
      platform: string;
      accountName: string;
      category: string;
      postType: string;
      caseState: string;
      complianceStatus: string;
    }>
  >;
  rowEditing: SocialMediaLink | null;
}

const MINIMUM_ROW_TO_MULTIPLE_EDIT = 2;

const renderInteractButtonList = ({
  isDisabledUpdate,
  handleClickUpdate,
  handleClickCancel,
}: InteractButtonHandlers) => [
  {
    text: 'Cancel',
    sx: {
      color: '#A3A7B0',
      '&:hover': {
        opacity: 0.8,
      },
    },
    variant: 'text',
    handleClick: handleClickCancel,
  },
  {
    text: 'Update',
    sx: {
      color: '#fff',
      backgroundColor: '#44AEFB',
      '&:hover': {
        backgroundColor: '#44AEFB',
        opacity: 0.8,
      },
    },
    disabled: isDisabledUpdate,
    variant: 'contained',
    handleClick: handleClickUpdate,
  },
];

const getColumns = ({
  control,
  errors,
}: IGetColumnDataMultipleRow): GridColDef<SocialMediaLink>[] => {
  return [
    {
      field: 'platform',
      headerName: 'Platform',
      minWidth: 133,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.platform,
      renderCell: () => {
        return (
          <StyledBox>
            <DropdownController
              name="platform"
              sx={{ ...defaultFormControlStyle }}
              control={control}
              defaultValue=""
              options={platformOptions}
              getOptionLabel={(option) =>
                platformMapper[option as SocialMediaPlatform] || (option as string)
              }
              errors={errors.category}
            />
            <StyledFormHelperText>{errors.category?.message}</StyledFormHelperText>
          </StyledBox>
        );
      },
      sortable: false,
    },
    {
      field: 'category',
      headerName: 'Category',
      minWidth: 133,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.category,
      renderCell: () => {
        return (
          <StyledBox>
            <DropdownController
              name="category"
              sx={{ ...defaultFormControlStyle }}
              control={control}
              defaultValue=""
              options={categoryOptions}
              getOptionLabel={(option) =>
                socialMediaCategoryMapper[option as SocialMediaCategory] || (option as string)
              }
              errors={errors.category}
            />
            <StyledFormHelperText>{errors.category?.message}</StyledFormHelperText>
          </StyledBox>
        );
      },
      sortable: false,
    },
    {
      field: 'postType',
      headerName: 'Post Type',
      minWidth: 94,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.postType,
      renderCell: () => (
        <StyledBox>
          <DropdownController
            name="postType"
            sx={{ ...defaultFormControlStyle }}
            control={control}
            defaultValue=""
            options={Object.values(PostType)}
            getOptionLabel={(option) => postTypeMapper[option as PostType] || (option as string)}
            errors={errors.postType}
          />
          <StyledFormHelperText>{errors.postType?.message}</StyledFormHelperText>
        </StyledBox>
      ),
      sortable: false,
    },
    {
      field: 'caseState',
      headerName: 'Case State',
      minWidth: 126,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.caseState,
      renderCell: () => (
        <StyledBox>
          <DropdownController
            name="caseState"
            sx={{ ...defaultFormControlStyle }}
            control={control}
            defaultValue=""
            options={Object.values(CaseState)}
            getOptionLabel={(option) => caseStateMapper[option as CaseState] || (option as string)}
            errors={errors.caseState}
          />
          <StyledFormHelperText>{errors.caseState?.message}</StyledFormHelperText>
        </StyledBox>
      ),
      sortable: false,
    },
    {
      field: 'complianceStatus',
      headerName: 'Compliance Status',
      minWidth: 181,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.complianceStatus,
      renderCell: () => (
        <StyledBox>
          <DropdownController
            name="complianceStatus"
            sx={{ ...defaultFormControlStyle }}
            control={control}
            defaultValue={null}
            options={['', ...Object.values(ComplianceStatusEnum)]}
            getOptionLabel={(option) =>
              option
                ? complianceStatusMapper[option as ComplianceStatusEnum] || (option as string)
                : 'N/A'
            }
            errors={errors.complianceStatus}
          />
          <StyledFormHelperText>{errors.complianceStatus?.message}</StyledFormHelperText>
        </StyledBox>
      ),
      sortable: false,
    },
    {
      field: 'detectionDate',
      headerName: 'Detection Date',
      minWidth: 190,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.lastEnforcementDate,
      renderCell: () => (
        <Controller
          name="detectionDate"
          control={control}
          defaultValue=""
          render={({ field: { value, ...others } }) => (
            <DatePickerStyled
              {...others}
              sx={{ ...defaultFormControlStyle, borderRadius: '8px' }}
              selected={value ? new Date(value) : null}
              calendarStartDay={1}
              monthsShown={1}
              placeholderText="DD.MM.YYYY"
              dateFormat="dd.MM.yyyy"
              selectsRange={false}
              isClearable
              onKeyDown={(e) => {
                e.preventDefault();
              }}
            />
          )}
        />
      ),
      sortable: false,
    },
  ];
};

const EditMultipleSocialLink = ({ listEdit, idSelected }: IEditMultipleSocialLink) => {
  const [open, setOpen] = useState(false);

  const {
    reset,
    control,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<EditMultipleSocialMediaLinkInput>({
    resolver: yupResolver(editMultipleSocialMediaLinkInputSchema),
    mode: 'onChange',
    reValidateMode: 'onSubmit',
  });
  const formValues = useWatch({ control });
  const { assetId } = useParams();

  const columns: GridColDef<SocialMediaLink>[] = useMemo(
    () =>
      getColumns({
        control,
        errors,
      }),
    [control, errors],
  );

  const isDisabledUpdate = useMemo(() => {
    return Object.keys(formValues || {}).every((key) => {
      const value = formValues?.[key as keyof EditMultipleSocialMediaLinkInput];
      return (
        (key !== 'complianceStatus' && value === '') ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
      );
    });
  }, [formValues]);

  const editMultipleSocialMediaLinksMutation = useEditMultipleSocialMediaLinks({
    mutationConfig: {
      onSuccess: () => {
        successMessage('Social Media Link has been edited!');
        onClose();
        reset();
      },
      onError: () => {
        errorMessage('Failed to edit Social Media Link');
      },
    },
  });

  const onClose = () => {
    reset();
    setOpen(false);
  };

  const handleClickEditMultipleRow = () => {
    if (listEdit.length < MINIMUM_ROW_TO_MULTIPLE_EDIT) return;
    setOpen(true);
  };

  const handleClickUpdate = async () => {
    const isValid = await trigger();
    if (Object.keys(errors).length !== 0 || !isValid) return;
    if (!assetId) {
      errorMessage('Model could not be found');
      return;
    }
    const payload = {
      ...Object.entries(getValues()).reduce((acc, [key, value]) => {
        if (value) {
          acc[key as keyof EditMultipleSocialMediaLinkInput] = value;
        }

        if (key === 'complianceStatus' && value !== null) {
          acc[key as keyof EditMultipleSocialMediaLinkInput] = value === '' ? null : value;
        }

        return acc;
      }, {} as Partial<EditMultipleSocialMediaLinkInput>),
      ids: idSelected,
    };
    editMultipleSocialMediaLinksMutation.mutate({ data: payload, modelId: assetId });
  };

  const handleClickCancel = () => {
    onClose();
  };

  return (
    <>
      <InteractButton
        onClick={handleClickEditMultipleRow}
        key="Edit"
        sx={{
          color: '#fff',
          backgroundColor: '#9DD049',
          maxWidth: 'fit-content',
          '&:hover': {
            backgroundColor: '#9DD049',
            opacity: 0.8,
          },
        }}
        text="Edit multiple"
        disabled={listEdit.length < MINIMUM_ROW_TO_MULTIPLE_EDIT}
        variant={'contained'}
      />
      <StyledModal
        maxWidth={false}
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: 'fit-content',
          },
        }}
        title="Edit multiple"
        onClose={onClose}
        open={open}
      >
        <Table<SocialMediaLink>
          dataTableWithoutCallingAPI={[listEdit[0]]}
          columns={columns.slice(0, 3)}
          selectedRowType={TableSelectType.SocialMediaLink}
          withoutSearch
          checkHidePagination
          tableSx={{
            height: '120px',
          }}
          customSxForTable={{
            width: '1000px',
            '& .MuiDataGrid-footerContainer': {
              display: 'none',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
            },
          }}
        />
        <Table<SocialMediaLink>
          dataTableWithoutCallingAPI={[listEdit[0]]}
          columns={columns.slice(-3)}
          selectedRowType={TableSelectType.SocialMediaLink}
          withoutSearch
          checkHidePagination
          tableSx={{
            height: '400px',
          }}
          customSxForTable={{
            width: '1000px',
            '& .MuiDataGrid-footerContainer': {
              display: 'none',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
            },
          }}
        />
        <Box display="flex" justifyContent="end" gap="14px">
          {renderInteractButtonList({ isDisabledUpdate, handleClickUpdate, handleClickCancel }).map(
            ({ text, sx, variant, disabled, handleClick }) => (
              <InteractButton
                onClick={handleClick}
                key={text}
                sx={sx}
                text={text}
                variant={variant as 'text' | 'outlined' | 'contained'}
                disabled={editMultipleSocialMediaLinksMutation.isLoading || disabled}
              />
            ),
          )}
        </Box>
      </StyledModal>
    </>
  );
};
export default EditMultipleSocialLink;
